if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');

        this.elements.viewer?.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails?.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            ?.addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.elements.thumbnails && this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) {
          this.removeListSemantic();
        }

        requestAnimationFrame(() => this.filterMediaBySelectedOptions());
      }

      onSlideChanged(event) {
        if (!this.elements.thumbnails || !event.detail.currentElement) return;

        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.getVisibleMediaById(mediaId) ||
          this.getFirstVisibleMedia() ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        const activeMediaId = activeMedia.dataset.mediaId;
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.getVisibleThumbnailByMediaId(activeMediaId);
            activeThumbnail?.parentElement?.firstChild !== activeThumbnail && activeThumbnail?.parentElement?.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          if (prepend) return;
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.getVisibleThumbnailByMediaId(activeMediaId);
        if (!activeThumbnail) return;
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      filterMediaBySelectedOptions(featuredMediaId = null, selectedColorValues = null) {
        if (!this.elements.viewer) return;

        const selectedColors = this.getSelectedColorOptionValues(selectedColorValues);
        const selectedColorTerms = this.getColorSearchTermsForValues(selectedColors);
        this.selectedColorValues = selectedColors;
        this.selectedColorTerms = selectedColorTerms;

        const viewerItems = this.getViewerItems();
        const selectedMediaIds = this.getMediaIdsForSelectedColors(viewerItems, selectedColors, selectedColorTerms);
        const featuredGalleryMediaId = this.getGalleryMediaId(featuredMediaId);

        if (selectedColors.length && selectedMediaIds.size === 0) {
          viewerItems.forEach((item) => {
            if (this.mediaMatchesSelectedColors(item, selectedColorTerms)) selectedMediaIds.add(item.dataset.mediaId);
          });
        }

        if (selectedColors.length && selectedMediaIds.size === 0) {
          viewerItems.forEach((item) => selectedMediaIds.add(item.dataset.mediaId));
        }

        viewerItems.forEach((item) => {
          this.setMediaVisibility(item, selectedMediaIds.has(item.dataset.mediaId));
        });

        this.getThumbnailItems().forEach((item) => {
          this.setMediaVisibility(item, selectedMediaIds.has(item.dataset.target));
        });

        this.refreshSlider(this.elements.viewer);
        this.refreshSlider(this.elements.thumbnails);

        const activeMedia = this.elements.viewer.querySelector('[data-media-id].is-active:not([hidden])');
        const firstVisibleMedia = this.getFirstVisibleMedia();

        if (!activeMedia && firstVisibleMedia) {
          this.setActiveMedia(firstVisibleMedia.dataset.mediaId, false);
        } else if (activeMedia) {
          this.setActiveThumbnail(this.getVisibleThumbnailByMediaId(activeMedia.dataset.mediaId));
        }

        this.filterModalContent(this.getProductModalContent(), featuredGalleryMediaId);
      }

      filterModalContent(modalContent, featuredMediaId = null) {
        if (!modalContent) return;

        const selectedColors = this.selectedColorValues || this.getSelectedColorOptionValues();
        const selectedColorTerms = this.selectedColorTerms || this.getColorSearchTermsForValues(selectedColors);
        const modalItems = Array.from(modalContent.querySelectorAll('[data-media-id]')).filter(
          (item) => item.parentElement === modalContent
        );
        const selectedRawMediaIds = this.getMediaIdsForSelectedColors(modalItems, selectedColors, selectedColorTerms);
        const featuredRawMediaId = this.getRawMediaId(featuredMediaId);

        if (selectedColors.length && selectedRawMediaIds.size === 0) {
          modalItems.forEach((item) => {
            if (this.mediaMatchesSelectedColors(item, selectedColorTerms)) {
              selectedRawMediaIds.add(String(item.dataset.mediaId));
            }
          });
        }

        if (featuredRawMediaId && selectedRawMediaIds.size > 0) {
          const featuredModalItem = modalItems.find((item) => String(item.dataset.mediaId) === featuredRawMediaId);
          if (featuredModalItem && !featuredModalItem.hidden) selectedRawMediaIds.add(featuredRawMediaId);
        }

        if (selectedColors.length && selectedRawMediaIds.size === 0) {
          modalItems.forEach((item) => selectedRawMediaIds.add(String(item.dataset.mediaId)));
        }

        modalItems.forEach((item) => {
          this.setMediaVisibility(item, selectedRawMediaIds.has(String(item.dataset.mediaId)));
        });
      }

      getSelectedColorOptionValues(selectedColorValues = null) {
        if (Array.isArray(selectedColorValues)) return selectedColorValues.filter(Boolean);
        if (selectedColorValues) return [selectedColorValues];
        if (this.dataset.selectedColorValue) return [this.dataset.selectedColorValue];

        const productInfo = this.closest('product-info');
        if (!productInfo) return [];

        const kitColors = Array.from(
          productInfo.querySelectorAll('[data-nb-kit-colors] [data-nb-color-value]:checked')
        ).map((input) => input.value);
        if (kitColors.length) return kitColors;

        const variantSelects = productInfo.querySelector('variant-selects');
        if (!variantSelects) return [];

        return Array.from(variantSelects.querySelectorAll('[data-nb-option-role="color"]'))
          .map((optionGroup) => {
            const select = optionGroup.querySelector('select');
            if (select) return select.value;

            return optionGroup.querySelector('input[type="radio"]:checked')?.value;
          })
          .filter(Boolean);
      }

      getMediaIdsForSelectedColors(items, selectedColors, selectedColorTerms) {
        const selectedMediaIds = new Set();
        if (!selectedColors.length) {
          items.forEach((item) => selectedMediaIds.add(this.getItemMediaId(item)));
          return selectedMediaIds;
        }

        items.forEach((item) => {
          const explicitColorTerms = this.getExplicitMediaColorTerms(item);
          if (!explicitColorTerms.length || !this.colorTermsMatch(explicitColorTerms, selectedColorTerms)) return;

          selectedMediaIds.add(this.getItemMediaId(item));
        });

        // The restored product photos have an explicit filename-to-color map. Using it first
        // prevents Shopify's upload order from mixing photos from neighboring variants.
        if (selectedMediaIds.size) return selectedMediaIds;

        const markers = this.getVariantMediaMarkers();
        if (!markers.length) return selectedMediaIds;

        items.forEach((item) => {
          const mediaPosition = Number(item.dataset.productMediaPosition);
          if (!mediaPosition) return;

          const closestMarker = this.getClosestVariantMediaMarker(mediaPosition, markers);
          if (!closestMarker || !this.colorTermsMatch(closestMarker.colorTerms, selectedColorTerms)) return;

          selectedMediaIds.add(this.getItemMediaId(item));
        });

        return selectedMediaIds;
      }

      getExplicitMediaColorTerms(item) {
        const image = item?.matches?.('img') ? item : item?.querySelector?.('img');
        const identifier = this.normalizeMediaIdentifier(
          [
            item?.dataset?.mediaSrc,
            item?.dataset?.mediaAlt,
            image?.currentSrc,
            image?.getAttribute?.('src'),
            image?.getAttribute?.('srcset'),
            image?.getAttribute?.('data-src'),
          ]
            .filter(Boolean)
            .join(' ')
        );

        if (!identifier) return [];

        const colorRules = [
          {
            value: 'Cinza Estrelado',
            tokens: ['073721', '074131', '074459', '074712', '074954', '075808', '110104am9'],
          },
          {
            value: 'Rosa com Corações',
            tokens: [
              '080618',
              '080812',
              '081039',
              '081230',
              '081542',
              '081912',
              '082252',
              '082432',
              '082945',
              '083558',
              '110314',
              '110317',
            ],
          },
          {
            value: 'Gota Azul',
            tokens: [
              '050510',
              '015617',
              '015811',
              '020056',
              '020326',
              '020603',
              '022243',
              '022844',
              '025702',
              '030245',
              '110307',
            ],
          },
          {
            value: 'Folha Salmão',
            tokens: ['103317', '104010', '104323', '104630', '013036', '013723', '014115', '014404', '014535', '110304', '110310'],
          },
          {
            value: 'Nuvem Azulada',
            tokens: ['061448', '062152', '062401', '110103am7'],
          },
          {
            value: 'Trigo Bege',
            tokens: ['064613', '065442', '065612', '070140', '110251'],
          },
          {
            value: 'Triângulo Azul',
            tokens: ['110104am10', 'ninhotrianguloazuloriginal'],
          },
          {
            value: 'Raminho Azul',
            tokens: ['110103am8', 'ninhoraminhoazuloriginal'],
          },
        ];
        const matchedRule = colorRules.find((rule) => rule.tokens.some((token) => identifier.includes(token)));

        return matchedRule ? this.getColorSearchTermsForValues([matchedRule.value]) : [];
      }

      normalizeMediaIdentifier(value = '') {
        try {
          return decodeURIComponent(value).toLowerCase().replace(/[^a-z0-9]+/g, '');
        } catch (error) {
          return value.toString().toLowerCase().replace(/[^a-z0-9]+/g, '');
        }
      }

      getVariantMediaMarkers() {
        if (this.variantMediaMarkers) return this.variantMediaMarkers;

        const colorOptionIndexes = this.getColorOptionIndexes();
        const seen = new Set();

        this.variantMediaMarkers = this.getVariantData()
          .map((variant) => {
            const mediaId = variant.featured_media_id || variant.featured_media?.id;
            const mediaPosition = Number(variant.featured_media_position || variant.featured_media?.position);
            const options = variant.options || [];
            const colorValues = colorOptionIndexes.map((index) => options[index]).filter(Boolean);

            if (!mediaId || !mediaPosition || !colorValues.length) return null;

            return {
              mediaId: String(mediaId),
              mediaPosition,
              colorTerms: this.getColorSearchTermsForValues(colorValues),
            };
          })
          .filter(Boolean)
          .filter((marker) => {
            const key = `${marker.mediaId}-${marker.mediaPosition}-${marker.colorTerms.join('|')}`;
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
          })
          .sort((first, second) => first.mediaPosition - second.mediaPosition);

        return this.variantMediaMarkers;
      }

      getClosestVariantMediaMarker(mediaPosition, markers) {
        return markers.reduce((closestMarker, marker) => {
          if (!closestMarker) return marker;

          const closestDistance = Math.abs(mediaPosition - closestMarker.mediaPosition);
          const markerDistance = Math.abs(mediaPosition - marker.mediaPosition);

          if (markerDistance < closestDistance) return marker;
          if (markerDistance === closestDistance && marker.mediaPosition <= mediaPosition) return marker;

          return closestMarker;
        }, null);
      }

      getVariantData() {
        if (this.variantData) return this.variantData;

        this.variantData =
          this.parseJsonScript(this.querySelector('[data-media-gallery-variants]')) ||
          this.parseJsonScript(this.closest('product-info')?.querySelector('[data-nb-product-variants]')) ||
          [];

        return this.variantData;
      }

      parseJsonScript(script) {
        if (!script) return null;

        try {
          return JSON.parse(script.textContent);
        } catch (error) {
          return null;
        }
      }

      getColorOptionIndexes() {
        if (this.colorOptionIndexes) return this.colorOptionIndexes;

        const productInfo = this.closest('product-info');
        const indexes = [];

        productInfo?.querySelectorAll('[data-nb-option-role="color"]').forEach((optionGroup) => {
          const position = Number(optionGroup.dataset.optionPosition);
          if (position) indexes.push(position - 1);
        });

        const kitColorPosition = Number(
          productInfo?.querySelector('[data-nb-kit-colors]')?.getAttribute('data-color-option-position')
        );
        if (kitColorPosition) indexes.push(kitColorPosition - 1);

        this.colorOptionIndexes = [...new Set(indexes)].filter((index) => index >= 0);
        return this.colorOptionIndexes;
      }

      mediaMatchesSelectedColors(item, selectedColors) {
        const mediaAlt = this.normalizeText(
          item.dataset.mediaAlt || item.querySelector('[data-media-alt]')?.dataset.mediaAlt || item.querySelector('img')?.alt
        );

        if (!mediaAlt) return false;

        return selectedColors.some((color) => mediaAlt.includes(color));
      }

      colorTermsMatch(mediaColorTerms, selectedColorTerms) {
        return mediaColorTerms.some((mediaColorTerm) =>
          selectedColorTerms.some(
            (selectedColorTerm) =>
              mediaColorTerm === selectedColorTerm ||
              mediaColorTerm.includes(selectedColorTerm) ||
              selectedColorTerm.includes(mediaColorTerm)
          )
        );
      }

      getColorSearchTerms(value = '') {
        const completeValue = this.normalizeText(value);
        const splitValues = value
          .toString()
          .split(/\s*(?:\+|\/|,|&|\be\b|\bcom\b)\s*/i)
          .map((part) => this.normalizeText(part))
          .filter(Boolean);

        return [completeValue, ...splitValues].filter(Boolean);
      }

      getColorSearchTermsForValues(values = []) {
        return [...new Set(values.flatMap((value) => this.getColorSearchTerms(value)))];
      }

      normalizeText(value = '') {
        return value
          .toString()
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, ' ')
          .trim();
      }

      getViewerItems() {
        return Array.from(this.elements.viewer?.querySelectorAll('[data-media-id]') || []);
      }

      getThumbnailItems() {
        return Array.from(this.elements.thumbnails?.querySelectorAll('[data-target]') || []);
      }

      getVisibleMediaById(mediaId) {
        return this.getViewerItems().find((item) => item.dataset.mediaId === mediaId && !item.hidden);
      }

      getVisibleThumbnailByMediaId(mediaId) {
        return this.getThumbnailItems().find((item) => item.dataset.target === mediaId && !item.hidden);
      }

      getFirstVisibleMedia() {
        return this.getViewerItems().find((item) => !item.hidden);
      }

      getItemMediaId(item) {
        return item.dataset.mediaId || item.dataset.target;
      }

      setMediaVisibility(item, isVisible) {
        if (!item) return;

        item.hidden = !isVisible;
        item.classList.toggle('hidden', !isVisible);
        item.setAttribute('aria-hidden', isVisible ? 'false' : 'true');
      }

      refreshSlider(sliderComponent) {
        if (!sliderComponent) return;

        sliderComponent.resetPages?.();

        const visibleItems = Array.from(sliderComponent.querySelectorAll('[id^="Slide-"]')).filter(
          (item) => !item.hidden
        );

        if (sliderComponent.currentPageElement) sliderComponent.currentPageElement.textContent = visibleItems.length ? 1 : 0;
        if (sliderComponent.pageTotalElement) sliderComponent.pageTotalElement.textContent = visibleItems.length;

        if (visibleItems.length <= 1) {
          sliderComponent.prevButton?.setAttribute('disabled', 'disabled');
          sliderComponent.nextButton?.setAttribute('disabled', 'disabled');
        }
      }

      getGalleryMediaId(mediaId) {
        if (!mediaId) return null;

        const mediaIdString = String(mediaId);
        if (mediaIdString.includes(`${this.sectionId}-`)) return mediaIdString;

        return `${this.sectionId}-${this.getRawMediaId(mediaIdString)}`;
      }

      getRawMediaId(mediaId) {
        if (!mediaId) return null;

        return String(mediaId).split('-').pop();
      }

      getProductModalContent() {
        return document
          .getElementById(`ProductModal-${this.sectionId}`)
          ?.querySelector('.product-media-modal__content');
      }

      get sectionId() {
        return this.id.replace('MediaGallery-', '');
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
